"use client";
/**
 * Driftsstatus / Service status — Svendetid marketing status page content.
 * Language-parametrized so /status (da) and /en/status (en) share one source.
 * Uses the shared svendetid-ds design system (same nav + footer as
 * /features, /pricing and /contact).
 */
import Link from "next/link";
import {
  dsFontClass, dsTokensCss, Icon, Footer, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

const SUPPORT_EMAIL = "kontakt@svendetid.dk";

const T = {
  da: {
    eyebrow: "Driftsstatus",
    h1: "Alle systemer kører.",
    sub: "Her kan du følge driften af Svendetid. Oplever du alligevel problemer, så skriv til os.",
    badge: "Alt fungerer normalt",
    services: [
      { icon: "cloud-outline", title: "Webapp", body: "Svendetid i browseren — dashboard, opgaver, fakturaer og kalender." },
      { icon: "phone-portrait-outline", title: "Mobilapp", body: "iOS- og Android-appen, inkl. tidsregistrering og synkronisering." },
      { icon: "swap-horizontal-outline", title: "API & synkronisering", body: "Realtidsopdateringer og datasynkronisering mellem enheder." },
      { icon: "sparkles", title: "SvendAI", body: "AI-assistenten og automatiske tilbud." },
    ],
    operational: "I drift",
    helpH2: "Virker noget ikke?",
    helpBody: "Skriv til os, så kigger vi på det med det samme.",
    contactUs: "Kontakt support",
    backHome: "Til forsiden",
  },
  en: {
    eyebrow: "Service status",
    h1: "All systems operational.",
    sub: "Follow the operational status of Svendetid here. If you're still having trouble, drop us a line.",
    badge: "Everything is running normally",
    services: [
      { icon: "cloud-outline", title: "Web app", body: "Svendetid in the browser — dashboard, jobs, invoices and calendar." },
      { icon: "phone-portrait-outline", title: "Mobile app", body: "The iOS and Android app, incl. time tracking and sync." },
      { icon: "swap-horizontal-outline", title: "API & sync", body: "Realtime updates and data synchronisation between devices." },
      { icon: "sparkles", title: "SvendAI", body: "The AI assistant and automatic offers." },
    ],
    operational: "Operational",
    helpH2: "Something not working?",
    helpBody: "Write to us and we'll look into it right away.",
    contactUs: "Contact support",
    backHome: "Back to home",
  },
} as const;

export default function StatusContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const home = lang === "en" ? "/en" : "/";
  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}
@keyframes ds-status-pulse { 0%,100% { box-shadow:0 0 0 0 rgba(74,222,128,0.45); } 60% { box-shadow:0 0 0 9px rgba(74,222,128,0); } }`}</style>

      <LandingNav lang={lang} />

      {/* ===== HERO ===== */}
      <section style={{ background: "var(--green-900)", color: "var(--text-on-hero)", paddingTop: 64 }}>
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "104px 32px 96px", textAlign: "center" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#F4C8A6" }}>{t.eyebrow}</div>
          <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 54, lineHeight: 1.06, letterSpacing: "-0.025em", margin: "18px 0 0", color: "#fff", textWrap: "balance" }}>{t.h1}</h1>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "34em", margin: "22px auto 0" }}>{t.sub}</p>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginTop: 32, padding: "12px 22px", borderRadius: 999, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)" }}>
            <span style={{ width: 10, height: 10, borderRadius: 999, background: "#4ADE80", animation: "ds-status-pulse 2.2s ease-out infinite" }} />
            <span style={{ fontSize: 15, fontWeight: 600, color: "#fff" }}>{t.badge}</span>
          </div>
        </div>
      </section>

      {/* ===== SERVICE CARDS ===== */}
      <section style={{ padding: "96px 0" }}>
        <div className="ds-2col" style={{ maxWidth: 920, margin: "0 auto", padding: "0 32px", display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 20 }}>
          {t.services.map((service) => (
            <div key={service.title} style={{
              background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18,
              padding: 28, boxShadow: "0 6px 16px rgba(16,33,27,0.06)", display: "flex",
              flexDirection: "column", gap: 14,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: "var(--green-100)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={service.icon} size={24} color="var(--brand)" />
                </div>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, color: "var(--green-700)", background: "var(--tint-mint)", padding: "6px 13px", borderRadius: 999 }}>
                  <Icon name="checkmark-circle" size={15} color="var(--green-600)" />
                  {t.operational}
                </span>
              </div>
              <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 21, letterSpacing: "-0.01em", margin: 0, color: "var(--green-900)" }}>{service.title}</h3>
              <p style={{ fontSize: 15, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 }}>{service.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== HELP BAND ===== */}
      <section style={{ padding: "0 0 96px" }}>
        <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ background: "var(--sage-50)", border: "1px solid var(--hairline)", borderRadius: 26, padding: "56px 48px", textAlign: "center" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 34, letterSpacing: "-0.02em", lineHeight: 1.1, margin: 0, color: "var(--green-900)" }}>{t.helpH2}</h2>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-muted)", maxWidth: "30em", margin: "14px auto 0" }}>{t.helpBody}</p>
            <div className="ds-cta-row" style={{ display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center", marginTop: 30 }}>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="btn-blue" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>
                {t.contactUs}<Icon name="arrow-forward" size={18} />
              </a>
              <Link href={home} className="btn-soft" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "var(--green-900)", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid var(--border)", background: "var(--sage-0)" }}>
                {t.backHome}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/status" enHref="/en/status" />
    </div>
  );
}
